import React, { useCallback, useState } from "react";
import classNames from "classnames";

import { IPhoneRuleConfig } from "../../../setup/api/services/phoneRuleConfigs/models";
import {
  findCountryByPhoneNumber,
  getMaxInputLength,
  sanitizePhoneDigits,
} from "../../../setup/util/phoneRuleConfig.helpers";
import CountrySelector from "./CountrySelector";
import styles from "./styles.module.scss";

interface Props {
  countries: IPhoneRuleConfig[];
  selectedCountry: IPhoneRuleConfig;
  onSelectCountry: (country: IPhoneRuleConfig) => void;
  /** National digits only (no country code), possibly with the optional leading zero. */
  phoneNumber: string;
  onPhoneChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  hint?: string;
  errorMessage?: string;
  disabled?: boolean;
}

// Digits copied from RTL text can land reversed; the party modal has always undone that.
const unreverseArabicPaste = (pasted: string) => {
  const digits = sanitizePhoneDigits(pasted);
  const hasArabic = /[؀-ۿ٠-٩]/.test(pasted);
  return hasArabic && digits.length > 1
    ? digits.split("").reverse().join("")
    : digits;
};

/**
 * Country picker + national number, ported from Waffy-web-next's login phone input and styled
 * to sit with the portal's TextInput. The country comes back as the whole config and the number
 * as national digits; the caller builds the E.164 value.
 */
const PhoneNumberInput = ({
  countries,
  selectedCountry,
  onSelectCountry,
  phoneNumber,
  onPhoneChange,
  onBlur,
  placeholder,
  hint,
  errorMessage,
  disabled,
}: Props) => {
  const [focused, setFocused] = useState(false);
  const maxLength = getMaxInputLength(selectedCountry);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Convert Arabic-Indic digits, drop non-digits and cap to the country's length.
      onPhoneChange(
        sanitizePhoneDigits(event.target.value).slice(0, maxLength)
      );
    },
    [maxLength, onPhoneChange]
  );

  // A pasted full number ("+20 115…", "00971…", "966…") switches the country to match it.
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pasted = event.clipboardData.getData("text").trim();
      const digits = unreverseArabicPaste(pasted);
      const isInternationalFormat = /^(\+|00)/.test(pasted);
      const withoutIntlPrefix = pasted.startsWith("00")
        ? digits.slice(2)
        : digits;

      const pastedCountry =
        isInternationalFormat || withoutIntlPrefix.length > maxLength
          ? findCountryByPhoneNumber(withoutIntlPrefix, countries)
          : undefined;

      if (pastedCountry) {
        if (pastedCountry.countryCode !== selectedCountry.countryCode) {
          onSelectCountry(pastedCountry);
        }
        onPhoneChange(
          withoutIntlPrefix
            .slice(pastedCountry.countryCode.length)
            .slice(0, getMaxInputLength(pastedCountry))
        );
        return;
      }
      onPhoneChange(withoutIntlPrefix.slice(0, maxLength));
    },
    [countries, maxLength, onPhoneChange, onSelectCountry, selectedCountry]
  );

  return (
    <div className={styles.outer}>
      <div className={styles.phoneRow}>
        <CountrySelector
          countries={countries}
          selectedCountry={selectedCountry}
          onSelect={onSelectCountry}
          disabled={disabled}
          hasError={!!errorMessage}
        />
        <div
          className={classNames(styles.field, {
            [styles.fieldFocused]: focused,
            [styles.fieldError]: !!errorMessage,
          })}>
          <span
            className={styles.prefix}>{`+${selectedCountry.countryCode}`}</span>
          <input
            className={styles.input}
            value={phoneNumber}
            onChange={handleChange}
            onPaste={handlePaste}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              onBlur?.();
            }}
            disabled={disabled}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder={placeholder}
          />
        </div>
      </div>

      {hint && <div className={styles.hint}>{hint}</div>}
      {errorMessage && (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{errorMessage}</p>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberInput;
